import * as PIXI from 'pixi.js';
import { deg2rad, IPoint } from './utils/math-utils';
import { game as app } from 'js/main';

const layoutsContext = require.context('js/layouts', true);

export const populate = (layout, raw, filter = {}): void => {
  const map = {};

  for (const model of raw.list) {
    if (model.className) {
      layout[model.className] = layout[model.className] || [];
    }

    if (filter[model.name] || filter[model.className] || (model.parent && !map[model.parent])) continue;

    let object;
    let view;

    if (model.type === ModelType.Layout) {
      const data = createInnerLayout(model);

      if (!data) continue;

      view = data.root;
      object = data.layout;
    } else {
      view = createView(model);
      object = view;
    }

    map[model.id] = view;
    map[model.parent] && map[model.parent].addChild(view);

    if (model.name) {
      layout[model.name] = object;
    }

    if (model.className) {
      layout[model.className].push(object);
    }
  }

  const rootModel = raw.list[0];
  const rootView = layout[rootModel.name];
  setRelativePosition(rootView, raw.x, raw.y, rootModel.x, rootModel.y);

  if (raw.handleResize) {
    addResizeHandler(rootView, raw.x, raw.y, rootModel.x, rootModel.y);
  }
};

const setRelativePosition = (view, percentX, percentY, x, y): void => {
  view.x = x + (percentX * app.screen.width) / 100;
  view.y = y + (percentY * app.screen.height) / 100;
};

const addResizeHandler = (view, percentX, percentY, x, y): void => {
  const handleResize = (): void => {
    if (view._destroyed) {
      unsubscribe();
      return;
    }

    setRelativePosition(view, percentX, percentY, x, y);
  };

  const unsubscribe = () => app.signals.onResize.remove(binding);
  const binding = app.signals.onResize.add(handleResize);
  view.onDestroyed.addOnce(unsubscribe);
};

export const playButtonSound = (): void => {
  // app.sounds.play('general_button_press');
};

export const createView = (model): PIXI.Container => {
  let view;

  switch (model.type) {
    case ModelType.Container:
      view = new PIXI.Container();
      copyContainerProps(view, model);
      break;

    case ModelType.Sprite:
      view = new PIXI.Sprite();
      copySpriteProps(view, model);

      if (view.buttonMode) {
        view.on('pointerdown', playButtonSound);
      }

      break;

    case ModelType.Text:
      view = new PIXI.Text("");
      copyTextProps(view, model);

      view.icons = model.icons.reduce((acc, icon) => {
        const iconView = new PIXI.Sprite();
        iconView.x = icon.x;
        iconView.y = icon.y;
        iconView.scale.x = icon.scale.x;
        iconView.scale.y = icon.scale.y;
        acc[icon.key] = iconView;

        iconView.texture = PIXI.Texture.from(icon.texture);

        return acc;
      }, {});
      break;

    case ModelType.NineSlicePlane:
      view = new PIXI.mesh.NineSlicePlane(PIXI.Texture.from(model.texture));
      copyNineSlicePlaneProps(view, model);
      break;

    case ModelType.Graphics:
      view = new PIXI.Graphics();
      copyGraphicsProps(view, model);

      if (model.bake) {
        const texture = view.generateCanvasTexture();
        view.destroy({ children: true });
        view = new PIXI.Sprite(texture);
        copyGraphicsProps(view, { ...model, commands: null });
      }

      break;

    default:
      console.warn(`Unknown type '${model.type}' in layout`);
      view = new PIXI.Container();
      break;
  }

  setHitArea(view, model);

  return view;
};

const createInnerLayout = (model): { layout: object; root: PIXI.Container } | null => {
  const key = model.path.replace('js/layouts', '.');
  const file = layoutsContext(key);

  if (!file) {
    return null;
  }

  const raw: Raw = file.raw;
  const { handleResize } = raw;

  raw.handleResize = model.handleResize;
  const layout = new file.Layout();
  raw.handleResize = handleResize;

  const rootView = layout[raw.list[0].name];
  copyContainerProps(rootView, model);
  setRelativePosition(rootView, model.percentX, model.percentY, model.x, model.y);

  if (model.handleResize) {
    addResizeHandler(rootView, model.percentX, model.percentY, model.x, model.y);
  }

  return { layout, root: rootView };
};

export const copyContainerProps = (container: PIXI.Container, model: ContainerModel): void => {
  container.name = model.name;
  container.x = model.x;
  container.y = model.y;
  container.scale.x = model.scale.x;
  container.scale.y = model.scale.y;
  container.pivot.x = model.pivot.x;
  container.pivot.y = model.pivot.y;
  container.angle = model.angle;
  container.alpha = model.alpha;
  container.visible = model.visible;
};

export const copySpriteProps = (sprite: PIXI.Sprite, model: SpriteModel): void => {
  sprite.name = model.name;
  sprite.x = model.x;
  sprite.y = model.y;
  sprite.anchor.x = model.anchor.x;
  sprite.anchor.y = model.anchor.y;
  sprite.scale.x = model.scale.x;
  sprite.scale.y = model.scale.y;
  sprite.angle = model.angle;
  sprite.alpha = model.alpha;
  sprite.visible = model.visible;
  sprite.tint = model.tint;
  sprite.blendMode = model.blendMode;
  sprite.interactive = model.interactive;
  sprite.buttonMode = model.buttonMode;
  sprite.texture = PIXI.Texture.from(model.texture);
};

export const copyTextProps = (text: PIXI.Text, model: TextModel): void => {
  text.name = model.name;
  text.x = model.x;
  text.y = model.y;
  text.anchor.x = model.anchor.x;
  text.anchor.y = model.anchor.y;
  text.scale.x = model.scale.x;
  text.scale.y = model.scale.y;
  text.angle = model.angle;
  text.alpha = model.alpha;
  text.visible = model.visible;
  text.blendMode = model.blendMode;
  text.interactive = model.interactive;
  text.buttonMode = model.buttonMode;
  text.maxWidth = model.maxWidth;
  text.maxHeight = model.maxHeight;
  text.maxFontSize = model.maxFontSize;
  text.text = model.localize ? app.getLocalized(model.text) : model.text;
  text.style = new PIXI.TextStyle(model.style);
};

export const copyGraphicsProps = (graphics: PIXI.Graphics, model: GraphicsModel): void => {
  graphics.name = model.name;
  graphics.x = model.x;
  graphics.y = model.y;
  graphics.scale.x = model.scale.x;
  graphics.scale.y = model.scale.y;
  graphics.pivot.x = model.pivot.x;
  graphics.pivot.y = model.pivot.y;
  graphics.angle = model.angle;
  graphics.alpha = model.alpha;
  graphics.visible = model.visible;
  graphics.tint = model.tint;
  graphics.blendMode = model.blendMode;
  graphics.interactive = model.interactive;
  graphics.buttonMode = model.buttonMode;

  if (model.commands) {
    for (const command of model.commands) {
      switch (command.type) {
        case GraphicsCommandType.BeginFill:
          graphics.beginFill(command.color, command.alpha);
          break;

        case GraphicsCommandType.LineStyle:
          graphics.lineStyle(command.width, command.color, command.alpha, command.alignment /* , command.native */);
          break;

        case GraphicsCommandType.DrawRect:
          graphics.drawRect(command.x, command.y, command.width, command.height);
          break;

        case GraphicsCommandType.DrawRoundedRect:
          graphics.drawRoundedRect(command.x, command.y, command.width, command.height, command.radius);
          break;

        case GraphicsCommandType.DrawCircle:
          graphics.drawCircle(command.x, command.y, command.radius);
          break;

        case GraphicsCommandType.DrawEllipse:
          graphics.drawEllipse(command.x, command.y, command.width, command.height);
          break;

        case GraphicsCommandType.DrawStar:
          graphics.drawStar(
            command.x,
            command.y,
            command.points,
            command.radius,
            command.innerRadius,
            deg2rad(command.rotation),
          );
          break;

        case GraphicsCommandType.DrawPolygon:
          graphics.drawPolygon(command.path.split(/\s*,\s*/).map(Number));
          break;

        case GraphicsCommandType.MoveTo:
          graphics.moveTo(command.x, command.y);
          break;

        case GraphicsCommandType.LineTo:
          graphics.lineTo(command.x, command.y);
          break;

        case GraphicsCommandType.Arc:
          graphics.arc(
            command.cx,
            command.cy,
            command.radius,
            deg2rad(command.startAngle),
            deg2rad(command.endAngle),
            command.anticlockwise,
          );
          break;

        case GraphicsCommandType.ArcTo:
          graphics.arcTo(command.x1, command.y1, command.x2, command.y2, command.radius);
          break;

        case GraphicsCommandType.QuadraticCurveTo:
          graphics.quadraticCurveTo(command.cpX, command.cpY, command.toX, command.toY);
          break;

        case GraphicsCommandType.BezierCurveTo:
          graphics.bezierCurveTo(command.cpX, command.cpY, command.cpX2, command.cpY2, command.toX, command.toY);
          break;

        case GraphicsCommandType.BeginHole:
          console.warn('Graphics hole is not supported by PIXI 4');
          // graphics.beginHole();
          break;

        case GraphicsCommandType.EndHole:
          // graphics.endHole();
          break;

        case GraphicsCommandType.ClosePath:
          graphics.closePath();
          break;

        case GraphicsCommandType.EndFill:
          graphics.endFill();
          break;
      }
    }
  }
};

export const copyNineSlicePlaneProps = (nineSlicePlane: PIXI.mesh.NineSlicePlane, model: NineSlicePlaneModel): void => {
  nineSlicePlane.name = model.name;
  nineSlicePlane.x = model.x;
  nineSlicePlane.y = model.y;
  nineSlicePlane.scale.x = model.scale.x;
  nineSlicePlane.scale.y = model.scale.y;
  nineSlicePlane.pivot.x = model.pivot.x;
  nineSlicePlane.pivot.y = model.pivot.y;
  nineSlicePlane.width = model.width;
  nineSlicePlane.height = model.height;
  nineSlicePlane.leftWidth = model.leftWidth;
  nineSlicePlane.topHeight = model.topHeight;
  nineSlicePlane.rightWidth = model.rightWidth;
  nineSlicePlane.bottomHeight = model.bottomHeight;
  nineSlicePlane.angle = model.angle;
  nineSlicePlane.alpha = model.alpha;
  nineSlicePlane.visible = model.visible;
  nineSlicePlane.tint = model.tint;
  nineSlicePlane.blendMode = model.blendMode;
  nineSlicePlane.interactive = model.interactive;
  nineSlicePlane.buttonMode = model.buttonMode;
  nineSlicePlane.texture = PIXI.Texture.from(model.texture);
};

const setHitArea = (view: PIXI.Container, model: Model) => {
  const hitArea = model.hitArea;

  if (!hitArea || hitArea.type === HitAreaType.None) return;

  switch (hitArea.type) {
    case HitAreaType.Rectangle:
      view.hitArea = new PIXI.Rectangle(hitArea.x, hitArea.y, hitArea.width, hitArea.height);
      break;

    case HitAreaType.Circle:
      view.hitArea = new PIXI.Circle(hitArea.x, hitArea.y, hitArea.radius);
      break;

    case HitAreaType.Ellipse:
      view.hitArea = new PIXI.Ellipse(hitArea.x, hitArea.y, hitArea.halfWidth, hitArea.halfHeight);
      break;

    case HitAreaType.Polygon:
      view.hitArea = new PIXI.Polygon(hitArea.path.split(/\s*,\s*/).map(Number));
      break;

    case HitAreaType.RoundedRectangle:
      view.hitArea = new PIXI.RoundedRectangle(hitArea.x, hitArea.y, hitArea.width, hitArea.height, hitArea.radius);
      break;
  }
};

// types
export enum ModelType {
  Container = 'Container',
  Sprite = 'Sprite',
  Text = 'Text',
  Graphics = 'Graphics',
  NineSlicePlane = 'NineSlicePlane',
  Layout = 'Layout',
}

export type CommonModel = {
  type: ModelType;
  id: string;
  name: string;
  className: string;
  x: number;
  y: number;
  scale: IPoint;
  angle: number;
  alpha: number;
  interactive: boolean;
  buttonMode: boolean;
  hitArea?: HitArea;
  visible: boolean;
  parent: string;
};

export type ContainerModel = CommonModel & {
  type: ModelType.Container;
  pivot: IPoint;
};

export type SpriteModel = CommonModel & {
  type: ModelType.Sprite;
  anchor: IPoint;
  tint: number;
  blendMode: number;
  texture: string;
};

export type TextIconModel = {
  key: string;
  x: number;
  y: number;
  scale: IPoint;
  texture: string;
};

export type TextModel = CommonModel & {
  type: ModelType.Text;
  anchor: IPoint;
  blendMode: number;
  text: string;
  localize: boolean;
  maxWidth: number;
  maxHeight: number;
  maxFontSize: number;
  icons: TextIconModel[];
  style: PIXI.TextStyleOptions;
};

export type GraphicsModel = CommonModel & {
  type: ModelType.Graphics;
  pivot: IPoint;
  tint: number;
  blendMode: number;
  bake: boolean;
  commands: GraphicsCommand[];
};

export type NineSlicePlaneModel = CommonModel & {
  type: ModelType.NineSlicePlane;
  scale: IPoint;
  pivot: IPoint;
  width: number;
  height: number;
  leftWidth: number;
  topHeight: number;
  rightWidth: number;
  bottomHeight: number;
  tint: number;
  blendMode: number;
  texture: string;
};

export type LayoutModel = CommonModel & {
  type: ModelType.Layout;
  path: string;
  percentX: number;
  percentY: number;
  handleResize: boolean;
  pivot: IPoint;
};

export type Model = ContainerModel | SpriteModel | TextModel | GraphicsModel | NineSlicePlaneModel | LayoutModel;

export type Raw = {
  x: number;
  y: number;
  handleResize: boolean;
  list: Model[];
  width: number;
  height: number;
};

enum GraphicsCommandType {
  BeginFill = 'beginFill',
  LineStyle = 'lineStyle',
  DrawRect = 'drawRect',
  DrawRoundedRect = 'drawRoundedRect',
  DrawCircle = 'drawCircle',
  DrawEllipse = 'drawEllipse',
  DrawStar = 'drawStar',
  DrawPolygon = 'drawPolygon',
  MoveTo = 'moveTo',
  LineTo = 'lineTo',
  Arc = 'arc',
  ArcTo = 'arcTo',
  QuadraticCurveTo = 'quadraticCurveTo',
  BezierCurveTo = 'bezierCurveTo',
  BeginHole = 'beginHole',
  EndHole = 'endHole',
  ClosePath = 'closePath',
  EndFill = 'endFill',
}

type GraphicsBeginFillCommand = {
  type: GraphicsCommandType.BeginFill;
  color: number;
  alpha: number;
};

type GraphicsLineStyleCommand = {
  type: GraphicsCommandType.LineStyle;
  width: number;
  color: number;
  alpha: number;
  alignment: number;
};

type GraphicsDrawRectCommand = {
  type: GraphicsCommandType.DrawRect;
  x: number;
  y: number;
  width: number;
  height: number;
};

type GraphicsDrawRoundedRectCommand = {
  type: GraphicsCommandType.DrawRoundedRect;
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
};

type GraphicsDrawCircleCommand = {
  type: GraphicsCommandType.DrawCircle;
  x: number;
  y: number;
  radius: number;
};

type GraphicsDrawEllipseCommand = {
  type: GraphicsCommandType.DrawEllipse;
  x: number;
  y: number;
  width: number;
  height: number;
};

type GraphicsDrawStarCommand = {
  type: GraphicsCommandType.DrawStar;
  x: number;
  y: number;
  points: number;
  radius: number;
  innerRadius: number;
  rotation: number;
};

type GraphicsDrawPolygonCommand = {
  type: GraphicsCommandType.DrawPolygon;
  path: string;
};

type GraphicsMoveToCommand = {
  type: GraphicsCommandType.MoveTo;
  x: number;
  y: number;
};

type GraphicsLineToCommand = {
  type: GraphicsCommandType.LineTo;
  x: number;
  y: number;
};

type GraphicsArcCommand = {
  type: GraphicsCommandType.Arc;
  cx: number;
  cy: number;
  radius: number;
  startAngle: number;
  endAngle: number;
  anticlockwise: boolean;
};

type GraphicsArcToCommand = {
  type: GraphicsCommandType.ArcTo;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  radius: number;
};

type GraphicsQuadraticCurveToCommand = {
  type: GraphicsCommandType.QuadraticCurveTo;
  cpX: number;
  cpY: number;
  toX: number;
  toY: number;
};

type GraphicsBezierCurveToCommand = {
  type: GraphicsCommandType.BezierCurveTo;
  cpX: number;
  cpY: number;
  cpX2: number;
  cpY2: number;
  toX: number;
  toY: number;
};

type GraphicsBeginHoleCommand = {
  type: GraphicsCommandType.BeginHole;
};

type GraphicsEndHoleCommand = {
  type: GraphicsCommandType.EndHole;
};

type GraphicsClosePathCommand = {
  type: GraphicsCommandType.ClosePath;
};

type GraphicsEndFillCommand = {
  type: GraphicsCommandType.EndFill;
};

type GraphicsCommand =
  | GraphicsBeginFillCommand
  | GraphicsLineStyleCommand
  | GraphicsDrawRectCommand
  | GraphicsDrawRoundedRectCommand
  | GraphicsDrawCircleCommand
  | GraphicsDrawEllipseCommand
  | GraphicsDrawStarCommand
  | GraphicsDrawPolygonCommand
  | GraphicsMoveToCommand
  | GraphicsLineToCommand
  | GraphicsArcCommand
  | GraphicsArcToCommand
  | GraphicsQuadraticCurveToCommand
  | GraphicsBezierCurveToCommand
  | GraphicsBeginHoleCommand
  | GraphicsEndHoleCommand
  | GraphicsClosePathCommand
  | GraphicsEndFillCommand;

enum HitAreaType {
  None = 'None',
  Rectangle = 'Rectangle',
  Circle = 'Circle',
  Ellipse = 'Ellipse',
  Polygon = 'Polygon',
  RoundedRectangle = 'RoundedRectangle',
}

type NoneHitArea = {
  type: HitAreaType.None;
};

type RectangleHitArea = {
  type: HitAreaType.Rectangle;
  x: number;
  y: number;
  width: number;
  height: number;
};

type CircleHitArea = {
  type: HitAreaType.Circle;
  x: number;
  y: number;
  radius: number;
};

type EllipseHitArea = {
  type: HitAreaType.Ellipse;
  x: number;
  y: number;
  halfWidth: number;
  halfHeight: number;
};

type PolygonHitArea = {
  type: HitAreaType.Polygon;
  path: string;
};

type RoundedRectangleHitArea = {
  type: HitAreaType.RoundedRectangle;
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
};

type HitArea =
  | NoneHitArea
  | RectangleHitArea
  | CircleHitArea
  | EllipseHitArea
  | PolygonHitArea
  | RoundedRectangleHitArea;
